// Utility to detect user's location via IP and set appropriate default currency
export const getLocationBasedCurrency = async () => {
  // ⚡ Preconnect dynamically (once per origin)
  const injectPreconnect = (url: string) => {
    const origin = new URL(url).origin
    if (!document.querySelector(`link[rel="preconnect"][href="${origin}"]`)) {
      const link = document.createElement('link')
      link.rel = 'preconnect'
      link.href = origin
      link.crossOrigin = 'anonymous'
      document.head.appendChild(link)
    }
  }

  // ✅ Step 1: Return saved preference if available
  if (typeof window !== 'undefined') {
    try {
      const saved = localStorage.getItem('userPreferences')
      if (saved) {
        const prefs = JSON.parse(saved)
        if (prefs.currency) {
          return { currency: prefs.currency, detected: true }
        }
      }
    } catch (error) {
      console.warn('Error parsing saved preferences:', error)
    }
  }

  // ✅ Step 2: Try primary geolocation service (geojs)
  const tryGeoAPI = async (url: string) => {
    injectPreconnect(url)

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 5000)

    try {
      const res = await fetch(url, {
        signal: controller.signal,
        headers: { Accept: 'application/json' },
      })

      if (!res.ok) {
        return null
      }

      const data = await res.json()
      const countryCode = data.country || data.country_code || data.countryCode

      // Egypt gets EGP, all other countries get USD
      if (countryCode === 'EG') return 'EGP'
      if (countryCode) return 'USD' // Any other valid country code defaults to USD
    } catch (err) {
      console.warn(`Failed to fetch from ${url}:`, err)
    } finally {
      clearTimeout(timeoutId)
    }

    return null
  }

  // Primary
  const primary = await tryGeoAPI('https://get.geojs.io/v1/ip/country.json')
  if (primary) return { currency: primary, detected: true }

  // ✅ Step 3: Fallback APIs
  const fallbackApis = [
    'https://ipapi.co/json/',
    'https://api.ipify.org?format=json',
  ]

  for (const url of fallbackApis) {
    const result = await tryGeoAPI(url)
    if (result) return { currency: result, detected: true }
  }

  // ✅ Step 4: Default fallback - USD for non-Egypt users, but mark as not detected
  return { currency: 'USD', detected: false }
}
